// User Management page
import React, { useState, useEffect } from "react";
import { Button } from "../atoms";
import { FormField } from "../molecules";
import { AdminLayout } from "../organisms";
import { userService } from "../../services/userService";
import { useAuth } from "../../contexts/AuthContext";

export const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "user",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await userService.getUsers();
      setUsers(data);
    } catch (err) {
      setError("Failed to load users");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (!formData.fullName.trim()) {
      setFormError("Full name is required");
      return;
    }

    if (!formData.email.trim()) {
      setFormError("Email is required");
      return;
    }

    if (formData.password.length < 8) {
      setFormError("Password must be at least 8 characters long");
      return;
    }

    try {
      setFormLoading(true);
      await userService.createUser(
        formData.fullName.trim(),
        formData.email.trim(),
        formData.password,
        formData.role,
      );
      setFormSuccess("User created successfully!");
      setFormData({ fullName: "", email: "", password: "", role: "user" });
      setShowCreateForm(false);
      await loadUsers();
    } catch (err) {
      setFormError(err.message || "Failed to create user");
    } finally {
      setFormLoading(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    setError("");
    try {
      await userService.updateUserRole(userId, role);
      await loadUsers();
    } catch (err) {
      setError(err.message || "Failed to update role");
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) {
      return;
    }
    setError("");
    try {
      await userService.deleteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message || "Failed to delete user");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <AdminLayout title="User Management">
      {/* Summary Card */}
      <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-ink-soft text-sm font-semibold uppercase tracking-wide">
              Total Users
            </h3>
            <p className="text-3xl font-bold text-terracotta-700">
              {users.length}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={loadUsers}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              className="!px-3 !py-1.5 !text-sm"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? "Cancel" : "Create New User"}
            </Button>
          </div>
        </div>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-2xl shadow-soft border border-terracotta-100/60 mb-6">
          <h3 className="text-xl font-semibold mb-4 text-terracotta-800">
            Create New User
          </h3>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <FormField
              label="Full Name"
              placeholder="Enter full name"
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
              required
            />
            <FormField
              label="Email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
            <FormField
              label="Password"
              type="password"
              placeholder="Enter password (min 8 characters)"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
            />
            <div>
              <label className="block text-sm font-semibold mb-2 text-ink-soft">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                className="w-full px-4 py-3 border border-terracotta-100 rounded-lg focus:outline-none focus:border-terracotta-500"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {formError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                {formError}
              </div>
            )}

            {formSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
                {formSuccess}
              </div>
            )}

            <Button
              variant="primary"
              type="submit"
              disabled={formLoading}
              className="w-full"
            >
              {formLoading ? "Creating..." : "Create User"}
            </Button>
          </form>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-2xl shadow-soft border border-terracotta-100/60 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-ink-soft">No users found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-soft uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-terracotta-100/40">
                {users.map((user) => (
                  <tr
                    key={user.id}
                    className="hover:bg-cream/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                      {user.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-terracotta-900">
                      {user.fullName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {currentUser && currentUser.userId === user.id ? (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.role === "admin"
                              ? "bg-terracotta-100 text-terracotta-800"
                              : "bg-cream text-ink-soft"
                          }`}
                        >
                          {user.role || "user"} (you)
                        </span>
                      ) : (
                        <select
                          value={user.role || "user"}
                          onChange={(e) =>
                            handleRoleChange(user.id, e.target.value)
                          }
                          className="px-2 py-1 border border-terracotta-100 rounded-lg text-sm focus:outline-none focus:border-terracotta-500"
                          aria-label={`Role for ${user.email}`}
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </select>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-soft">
                      {user.createdAt ? formatDate(user.createdAt) : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      {currentUser && currentUser.userId === user.id ? (
                        <span className="text-ink-soft text-xs">—</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="px-3 py-1 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
